<template>
  <ErrorBoundary>
    <div>
      <LoginPage v-if="!auth.user.value && shouldShowLogin" />
      <Layout 
        v-else-if="auth.user.value"
        :current-view="currentView" 
        :current-tab="currentTab"
        @view-change="setCurrentView"
      >
        <!-- Dynamic dashboard component based on user role -->
        <component 
          v-if="route.name === 'Dashboard'" 
          :is="dashboardComponent" 
          :key="`dashboard-${auth.user.value?.role || 'default'}`" 
          :userId="auth.user.value?.id"
          @tab-change="setCurrentTab" 
        />
        <!-- Other routes -->
        <router-view 
          v-else
          :key="$route.path" 
          @tab-change="setCurrentTab" 
        />
        <Toaster />
        <ToastContainer />
      </Layout>
    </div>
  </ErrorBoundary>
</template>

<script>
import { inject, ref, computed, watch, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LoginPage from '../views/LoginPage.vue'
import Layout from './Layout.vue'
import ErrorBoundary from './ErrorBoundary.vue'
import Toaster from './ui/sonner.vue'
import ToastContainer from './ui/toast-container.vue'

// Dynamic imports for dashboard components
const EmployeeDashboard = defineAsyncComponent(() => import('../views/dashboards/EmployeeDashboard.vue'))
const ManagerDashboard = defineAsyncComponent(() => import('../views/dashboards/ManagerDashboard.vue'))
const AdminDashboard = defineAsyncComponent(() => import('../views/dashboards/AdminDashboard.vue'))

export default {
  name: 'AppContent',
  components: {
    LoginPage,
    Layout,
    ErrorBoundary,
    Toaster,
    ToastContainer,
    EmployeeDashboard,
    ManagerDashboard,
    AdminDashboard
  },
  setup() {
    const auth = inject('auth')
    const route = useRoute()
    const router = useRouter()
    const currentTab = ref(null)

    // Determine if login page should be shown
    const shouldShowLogin = computed(() => {
      // Only show login if user is not authenticated AND we're on the login route
      return !auth.user.value && route.name === 'Login'
    })

    // Watch for route changes to handle back button navigation
    watch(() => route.name, (newRouteName) => {
      // If user navigates to login page while authenticated, the router guard will handle it
      // This is just for additional safety
      if (newRouteName === 'Login' && auth.user.value) {
        console.log('Authenticated user attempted to access login page')
      }
    })

    // Dynamic dashboard component based on user role
    const dashboardComponent = computed(() => {
      if (!auth.user.value) return EmployeeDashboard
      
      switch (auth.user.value.role) {
        case 'admin':
          return AdminDashboard
        case 'manager':
          return ManagerDashboard
        case 'employee':
        default:
          return EmployeeDashboard
      }
    })

    // Get current view from route name
    const currentView = computed(() => {
      if (!route.name) return 'dashboard'
      
      switch (route.name) {
        case 'Dashboard':
          return 'dashboard'
        case 'TeamBoard':
          return 'teams'
        case 'ScheduleManager':
          return 'schedule'
        case 'PayrollCalculator':
          return 'payroll'
        case 'ProfileSettings':
          return 'profile'
        case 'CreateUser':
          return 'create-user'
        case 'Organization':
          return 'organization'
        case 'UserTasks':
          return 'tasks'
        case 'ManagerTasks':
          return 'manager-tasks'
        case 'AdminTasks':
          return 'admin-tasks'
        default:
          return 'dashboard'
      }
    })

    const setCurrentView = (view) => {
      // This is now handled by router navigation in Layout component
      console.log('View change requested:', view)
    }

    const setCurrentTab = (tab) => {
      currentTab.value = tab
    }

    return {
      auth,
      route,
      currentView,
      currentTab,
      shouldShowLogin,
      setCurrentView,
      setCurrentTab,
      dashboardComponent
    }
  }
}
</script>